import React from 'react';
import { FlexRow, ButtonProps, BodyTextSmall } from '@minecraft.atoms';
import UncontrolledTooltip from '../components/UncontrolledTooltip';
import { Button } from '../atoms/Button/Button';

export type PickButtonProps = Omit<ButtonProps, 'color' | 'variant' | 'startIcon' | 'endIcon' | 'label'> & {
  /** required unique id to facilitate tooltip */
  id: string;
  /** list of the names of users who have picked the talent */
  pickedByNames: string;
  /** whether to show picked by names inline or as a tooltip */
  withTooltip?: boolean;
  /** number of picks for the talent this is shown for */
  picksCount: number | string;
};

type PickedByProps = Pick<PickButtonProps, 'id' | 'pickedByNames' | 'withTooltip'>;

const PickedBy = ({ withTooltip, id, pickedByNames }: PickedByProps) => {
  if (!pickedByNames || typeof pickedByNames !== 'string') return null;

  if (withTooltip) {
    return (
      <UncontrolledTooltip placement="top" target={`pick-${id}`}>
        {pickedByNames}
      </UncontrolledTooltip>
    );
  }

  return <BodyTextSmall data-testid="picked-by-inline">{pickedByNames}</BodyTextSmall>;
};

export const PickButton = ({
  id,
  pickedByNames,
  picksCount = 0,
  withTooltip = false,
  fixedWidth = true,
  ...props
}: PickButtonProps) => {
  if (!id) {
    return null;
  }

  return (
    <FlexRow
      alignItems="center"
      wrap="nowrap"
      gap={!withTooltip && pickedByNames ? '2' : undefined}
      data-testid={`${id}-wrapper`}
    >
      <Button
        {...props}
        id={`pick-${id}`}
        color="primary"
        variant="secondary"
        label={{ name: picksCount?.toString(), justify: 'start' }}
        startIcon={{ name: 'thumbs-up', justify: 'end' }}
        endIcon={undefined}
        fixedWidth={fixedWidth}
      />
      <PickedBy withTooltip={withTooltip} id={id} pickedByNames={pickedByNames} />
    </FlexRow>
  );
};
